import { useState } from "react";
import { useApiQuery } from "../../lib/useApiQuery.js";
import { usersPath } from "../../lib/api-paths.js";
import type { PublicUser, UsersListResponse } from "../../lib/api-types.js";
import { Modal } from "../../components/Modal.js";
import {
  FormField,
  PrimaryButton,
  SecondaryButton,
  TextInput,
} from "../../components/FormField.js";

/**
 * 组织搭建里复用的「搜索 + 多选同事」弹窗。
 * 负责人选择（Step4）与管理员选择（Step5）共用，只换标题。
 */
export function UserPickerModal({
  title,
  orgCode,
  initial,
  saving,
  onCancel,
  onSave,
}: {
  title: string;
  orgCode: string;
  initial: string[];
  saving: boolean;
  onCancel: () => void;
  onSave: (userIds: string[]) => void | Promise<void>;
}): JSX.Element {
  const [selected, setSelected] = useState<string[]>(initial);
  const [q, setQ] = useState("");

  const qs = new URLSearchParams({ pageSize: "200", status: "active" });
  if (q.trim()) qs.set("q", q.trim());
  const { data, loading } = useApiQuery<UsersListResponse>(
    `${usersPath(orgCode)}?${qs.toString()}`,
    [orgCode, q],
  );

  const toggle = (id: string): void =>
    setSelected((curr) =>
      curr.includes(id) ? curr.filter((x) => x !== id) : [...curr, id],
    );

  return (
    <Modal
      open
      onClose={onCancel}
      title={title}
      size="lg"
      footer={
        <>
          <SecondaryButton onClick={onCancel}>取消</SecondaryButton>
          <PrimaryButton disabled={saving} onClick={() => void onSave(selected)}>
            {saving ? "保存中…" : `保存（已选 ${selected.length}）`}
          </PrimaryButton>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <FormField label="搜索同事" hint="按用户名 / 飞书姓名">
          <TextInput value={q} onChange={(e) => setQ(e.target.value)} />
        </FormField>
        {loading ? (
          <p className="text-sm text-dosia-text-dim">加载中…</p>
        ) : (
          <ul className="max-h-[320px] divide-y divide-dosia-border-light overflow-auto rounded-md border border-dosia-border">
            {(data?.rows ?? []).map((u: PublicUser) => (
              <li
                key={u.id}
                className="flex items-center gap-2 px-3 py-2 hover:bg-dosia-bg-lighter"
              >
                <input
                  type="checkbox"
                  checked={selected.includes(u.id)}
                  onChange={() => toggle(u.id)}
                  aria-label={`选择 ${u.user}`}
                />
                <span className="text-sm text-dosia-text">
                  {u.feishu?.name ?? u.user}
                </span>
                {u.feishu?.name ? (
                  <span className="font-mono text-xs text-dosia-text-dim">
                    {u.user}
                  </span>
                ) : null}
              </li>
            ))}
            {(data?.rows ?? []).length === 0 ? (
              <li className="px-3 py-4 text-center text-sm text-dosia-text-dim">
                无匹配同事
              </li>
            ) : null}
          </ul>
        )}
      </div>
    </Modal>
  );
}
